/**
 * PluginAny Trip Planner — rangeEngine.js
 * Physics-based EV range and charging predictor
 *
 * Inputs: vehicle, route segments, weather, settings
 * Outputs: SoC at each point + confidence interval + charging stop recommendations
 *
 * Built for: PluginAny / Zenvio Mobility — v1 (May 2026)
 *
 * v2 update (2026-05-17): tire pressure model upgraded with citations.
 *   See /src/lib/tirePressureV2.js for the production-grade temperature-,
 *   permeation-, and warming-aware tire pressure model with peer-reviewed
 *   citations (Schwall 2016, Zhu 2019, Persson 2002, Waddell 2009, Heliyon).
 */

import { estimateEffectivePressure } from './tirePressureV2.js';
import { driveCycleMultiplier } from './driveCycle.js';

// ============================================================================
// CONSTANTS
// ============================================================================
const G = 9.81;                    // m/s²
const AIR_DENSITY_15C = 1.225;     // kg/m³ at sea level, 15°C
const SECONDS_PER_HOUR = 3600;

// ============================================================================
// AIR DENSITY — varies with temperature and altitude
// ============================================================================
function airDensity(temp_c, altitude_m = 0) {
  // ISA standard atmosphere approximation
  const temp_k = 273.15 + temp_c;
  const pressure_pa = 101325 * Math.pow(1 - 0.0065 * altitude_m / 288.15, 5.255);
  return pressure_pa / (287.05 * temp_k);  // ideal gas, dry air
}

// ============================================================================
// HEAT PUMP / HVAC POWER
// ============================================================================
function hvacPower_kW(ambient_c, hvac_setting, heat_pump_quality) {
  if (hvac_setting === 'off') return 0;

  const cabin_target = 21;  // °C, default
  const delta = Math.abs(ambient_c - cabin_target);

  // Cooling case (ambient warmer than cabin)
  if (ambient_c > cabin_target) {
    // AC always works, scales with delta
    return Math.min(3.0, 1.0 + 0.10 * delta);  // ~1 kW base, up to 3 kW
  }

  // Heating case (ambient colder than cabin)
  switch (heat_pump_quality) {
    case 'modern':
      // Tesla Octovalve, Hyundai E-GMP, VW MEB heat pumps
      // Efficient down to -20°C
      if (ambient_c > -10) return Math.min(1.5, 0.5 + 0.05 * delta);
      if (ambient_c > -20) return Math.min(2.2, 1.0 + 0.08 * delta);
      return 3.0;  // very cold, even modern pumps struggle

    case 'first_gen':
      // Early Leaf, Zoe — heat pump works moderate cold, falls back to resistive
      if (ambient_c > 0) return Math.min(1.8, 0.7 + 0.06 * delta);
      return Math.min(3.5, 1.5 + 0.10 * delta);  // falls back to resistive

    case 'none':
    default:
      // Pure resistive heating
      return Math.min(4.0, 0.8 + 0.12 * delta);
  }
}

// ============================================================================
// BATTERY TEMPERATURE COEFFICIENT
// ============================================================================
function batteryTempEfficiency(ambient_c, battery_type) {
  // Returns multiplier on effective battery capacity at given temp.
  // LFP suffers more in cold than NMC.
  if (battery_type === 'LFP') {
    if (ambient_c < -10) return 0.78;
    if (ambient_c < 0)   return 0.88;
    if (ambient_c < 10)  return 0.95;
    if (ambient_c < 30)  return 1.00;
    if (ambient_c < 40)  return 0.97;
    return 0.94;  // hot, slight degradation
  }
  if (battery_type === 'NMC' || battery_type === 'NCA' || battery_type === 'LMFP') {
    if (ambient_c < -10) return 0.85;
    if (ambient_c < 0)   return 0.92;
    if (ambient_c < 10)  return 0.97;
    if (ambient_c < 30)  return 1.00;
    if (ambient_c < 40)  return 0.98;
    return 0.95;
  }
  // Unknown chemistry — conservative default
  if (ambient_c < 0) return 0.90;
  if (ambient_c > 35) return 0.96;
  return 1.00;
}

// ============================================================================
// WIND COMPONENT projected onto route direction
// ============================================================================
function windComponentAlongRoute(wind_speed_ms, wind_direction_deg, route_bearing_deg) {
  // wind_direction: meteorological convention — direction wind is FROM
  // Wind FROM the same direction as travel = blowing INTO the vehicle = headwind.
  // Returns the wind component along the direction of travel:
  //   positive = headwind (opposes motion, increases drag)
  //   negative = tailwind (aids motion, decreases drag)
  //
  // Fixed 2026-05-17: previously had a `+ 180` term that inverted the sign,
  // causing headwinds to be modeled as tailwinds and vice versa.
  const relative_angle_rad = ((wind_direction_deg - route_bearing_deg) * Math.PI) / 180;
  return wind_speed_ms * Math.cos(relative_angle_rad);
}

// ============================================================================
// TIRE PRESSURE — affects rolling resistance
// ============================================================================
// v1 helper: simple ratio-based multiplier. Kept for backward compatibility,
// for callers that have only current_kpa + spec_kpa with no temperature etc.
function tirePressurePenalty(current_kpa, spec_kpa) {
  // Returns a multiplier applied to roll_cr.
  // If either value is missing or invalid, assume spec (no penalty).
  if (!current_kpa || !spec_kpa || current_kpa <= 0 || spec_kpa <= 0) return 1.0;
  
  const ratio = current_kpa / spec_kpa;
  
  // Over-inflated: small efficiency gain, capped at -8%.
  // (real-world: under-inflated tires cause comfort/grip problems too,
  // but the planner only models energy, not those secondary effects)
  if (ratio >= 1.0) {
    return Math.max(0.92, 1.0 - (ratio - 1.0) * 0.20);
  }
  
  // Under-inflated: roll_cr rises sharply.
  // Tire literature (SAE/Continental): each 10% reduction in pressure
  // raises rolling resistance by ~6-8%.
  // So roll_cr_multiplier = 1 + (1 - ratio) × 0.7
  // Example: ratio=0.85 (15% low) → multiplier = 1.10 (10% more roll_cr)
  //          ratio=0.70 (30% low) → multiplier = 1.21 (21% more roll_cr)
  return 1.0 + (1.0 - ratio) * 0.70;
}

// v2: integrates temperature, permeation, in-trip warming, with cited
// constants. Returns BOTH the Crr multiplier AND the audit breakdown so the
// UI can show why the engine reached its conclusion.
//
// Falls back to the v1 helper if spec_kpa is missing (vehicle data gap).
function tirePressurePenaltyV2(opts) {
  const {
    spec_kpa, current_kpa, ambient_c,
    days_since_check, trip_duration_min, avg_speed_kmh,
    inflation_gas = 'air',
    measured_frequency_hz, tire_size_code,
  } = opts;
  
  if (!spec_kpa || spec_kpa <= 0) {
    return { multiplier: 1.0, audit: null };
  }
  
  const result = estimateEffectivePressure({
    spec_kpa,
    user_input_kpa: current_kpa,
    ambient_temp_c: ambient_c,
    days_since_last_check: days_since_check,
    trip_duration_min,
    avg_speed_kmh,
    inflation_gas,
    measured_frequency_hz,
    tire_size_code,
  });
  
  return { multiplier: result.crr_multiplier, audit: result };
}

// ============================================================================
// DRIVETRAIN — affects efficiency and effective mass
// ============================================================================
function drivetrainEfficiency(drivetrain_type, base_eff, decouple_at_cruise, avg_speed_kmh) {
  // If vehicle data lacks drivetrain_type, fall back to provided base_eff.
  if (!drivetrain_type) return base_eff || 0.92;
  
  const baseline = base_eff || 0.94;
  
  switch (drivetrain_type) {
    case 'RWD':
    case 'FWD':
      // Single motor, single reduction gear
      return baseline;
    
    case 'AWD_dual':
      // Two motors; modern AWDs can decouple front motor at cruise
      if (decouple_at_cruise && avg_speed_kmh > 50) {
        return baseline - 0.01;  // ~1% loss vs single-motor at cruise
      }
      return baseline - 0.03;    // ~3% loss without decoupling
    
    case 'AWD_quad':
      // Four motors (Rivian, Lucid Sapphire)
      return baseline - 0.04;
    
    default:
      return baseline - 0.02;
  }
}

function drivetrainMassAdder_kg(drivetrain_type) {
  // Extra mass for additional motors + gearboxes vs RWD baseline.
  // Used when vehicle.mass_kg refers to a base/RWD variant and we model an AWD variant.
  switch (drivetrain_type) {
    case 'AWD_dual': return 90;
    case 'AWD_quad': return 140;
    default: return 0;
  }
}

// ============================================================================
// ROAD QUALITY — multiplier on roll_cr for surface roughness
// ============================================================================
function roadQualityMultiplier(road_quality) {
  // Optional per-segment or per-trip modifier.
  // 1.0 = baseline (good highway, well-maintained)
  // 1.05-1.15 = Indian rural highway, mixed surface
  // 1.20+ = poor unpaved sections (off-route; usually not in trip planning)
  switch (road_quality) {
    case 'excellent': return 0.97;
    case 'good': return 1.00;
    case 'fair': return 1.08;
    case 'poor': return 1.18;
    default: return 1.00;
  }
}

// ============================================================================
// CORE: SEGMENT ENERGY (PHYSICS MODE)
// ============================================================================
function segmentEnergyPhysics(segment, vehicle, conditions) {
  const {
    distance_km, avg_speed_kmh, gradient_pct, segment_bearing_deg, road_quality
  } = segment;
  const {
    mass_kg, drag_cd, frontal_area_m2, roll_cr,
    drivetrain_eff, regen_eff,
    heat_pump_quality, battery_type,
    drivetrain_type, drivetrain_decouple,
    tire_pressure_spec_kpa
  } = vehicle;
  const {
    ambient_c, altitude_m, wind_speed_kmh, wind_direction_deg,
    hvac_setting, driving_style_multiplier,
    tire_pressure_actual_kpa,
    // v2 context — all optional. When present, the engine uses the cited
    // temperature/permeation/warming model from tirePressureV2.js.
    days_since_tire_check,
    trip_duration_min,
    inflation_gas,
    measured_frequency_hz,
    tire_size_code,
    // Drive-cycle context — all optional. When country_code is present, the
    // engine applies a Heliyon 2024 drive-cycle multiplier to E_drive based
    // on segment average speed + locale road-quality adjustment.
    country_code,
    road_type
  } = conditions;

  const v_road = avg_speed_kmh / 3.6;
  const wind_along = windComponentAlongRoute(
    wind_speed_kmh / 3.6, wind_direction_deg, segment_bearing_deg
  );
  const v_eff_for_drag = v_road + wind_along;
  const time_s = (distance_km * 1000) / v_road;

  const eff_mass_kg = mass_kg + drivetrainMassAdder_kg(drivetrain_type);

  // Tire pressure: use v2 when ambient_c is present (the engine has temperature
  // context); otherwise fall back to v1 ratio-based penalty. v1 is also still
  // used when tire_pressure_spec_kpa is missing (vehicle data gap).
  let tire_mult;
  let tire_audit = null;
  if (ambient_c != null && tire_pressure_spec_kpa) {
    const v2 = tirePressurePenaltyV2({
      spec_kpa: tire_pressure_spec_kpa,
      current_kpa: tire_pressure_actual_kpa,
      ambient_c,
      days_since_check: days_since_tire_check,
      trip_duration_min,
      avg_speed_kmh,
      inflation_gas,
      measured_frequency_hz,
      tire_size_code,
    });
    tire_mult = v2.multiplier;
    tire_audit = v2.audit;
  } else {
    tire_mult = tirePressurePenalty(tire_pressure_actual_kpa, tire_pressure_spec_kpa);
  }

  const road_mult = roadQualityMultiplier(road_quality);
  const eff_roll_cr = roll_cr * tire_mult * road_mult;

  const eff_drivetrain = drivetrainEfficiency(
    drivetrain_type, drivetrain_eff, drivetrain_decouple, avg_speed_kmh
  );

  const rho = airDensity(ambient_c, altitude_m);
  const F_drag = 0.5 * rho * drag_cd * frontal_area_m2 * v_eff_for_drag * Math.abs(v_eff_for_drag);
  const theta = Math.atan(gradient_pct / 100);
  const F_roll = eff_roll_cr * eff_mass_kg * G * Math.cos(theta);
  const F_grade = eff_mass_kg * G * Math.sin(theta);

  const P_wheel = (F_drag + F_roll + F_grade) * v_road;

  let E_drive_wh;
  if (P_wheel >= 0) {
    E_drive_wh = (P_wheel / eff_drivetrain) * (time_s / SECONDS_PER_HOUR);
  } else {
    E_drive_wh = P_wheel * regen_eff * (time_s / SECONDS_PER_HOUR);
  }

  // Drive-cycle correction: captures stop-and-go losses that the constant-
  // speed physics model misses. Anchored on Heliyon 2024 empirical data
  // (WLTP/NEDC/MIDC/IDC). Applied only to E_drive (HVAC is independent
  // of drive cycle). See src/lib/driveCycle.js for the speed→multiplier curve.
  const dc = driveCycleMultiplier({
    avg_speed_kmh,
    country_code,
    road_type
  });
  E_drive_wh *= dc.multiplier;

  const hvac_kW = hvacPower_kW(ambient_c, hvac_setting, heat_pump_quality);
  const E_hvac_wh = hvac_kW * 1000 * (time_s / SECONDS_PER_HOUR);

  const total_wh = E_drive_wh * driving_style_multiplier + E_hvac_wh;

  return {
    energy_wh: total_wh,
    drive_wh: E_drive_wh * driving_style_multiplier,
    hvac_wh: E_hvac_wh,
    time_s,
    mode: 'physics',
    diagnostics: {
      tire_mult,
      tire_audit,
      road_mult,
      eff_roll_cr,
      eff_drivetrain,
      eff_mass_kg,
      drive_cycle_mult: dc.multiplier,
      drive_cycle_audit: dc.audit
    }
  };
}

// ============================================================================
// FALLBACK: EMPIRICAL ENERGY (when physics fields missing)
// ============================================================================
function segmentEnergyEmpirical(segment, vehicle, conditions) {
  const { distance_km, avg_speed_kmh, gradient_pct } = segment;
  const { consumption_wh_km, mass_kg } = vehicle;
  const { ambient_c, wind_speed_kmh, hvac_setting, driving_style_multiplier } = conditions;

  // Temperature factor
  let temp_factor = 1.0;
  if (ambient_c < -10) temp_factor = 1.6;
  else if (ambient_c < 0) temp_factor = 1.4;
  else if (ambient_c < 10) temp_factor = 1.2;
  else if (ambient_c < 25) temp_factor = 1.0;
  else if (ambient_c < 35) temp_factor = 1.05;
  else if (ambient_c < 45) temp_factor = 1.15;
  else temp_factor = 1.25;

  // Speed factor
  let speed_factor = 1.0;
  if (avg_speed_kmh > 100) speed_factor = 1.25;
  else if (avg_speed_kmh > 80) speed_factor = 1.15;
  else if (avg_speed_kmh > 40) speed_factor = 1.05;

  // Gradient factor (simplified)
  const gradient_factor = 1.0 + 0.05 * Math.max(0, gradient_pct) - 0.03 * Math.max(0, -gradient_pct);

  // Wind factor (very rough in empirical mode)
  const wind_factor = 1.0 + 0.005 * wind_speed_kmh;  // ~5% per 10 km/h headwind

  // HVAC additive (kWh per hour)
  const time_h = distance_km / avg_speed_kmh;
  let hvac_kWh = 0;
  if (hvac_setting !== 'off') {
    hvac_kWh = (ambient_c < 5 ? 2.5 : ambient_c > 30 ? 2.0 : 1.0) * time_h;
  }

  const drive_wh = consumption_wh_km * distance_km * temp_factor * speed_factor *
                   gradient_factor * wind_factor * driving_style_multiplier;
  const hvac_wh = hvac_kWh * 1000;

  return {
    energy_wh: drive_wh + hvac_wh,
    drive_wh,
    hvac_wh,
    time_s: time_h * SECONDS_PER_HOUR,
    mode: 'empirical'
  };
}

// ============================================================================
// MODEL SELECTOR — physics if fields present, fallback to empirical
// ============================================================================
function selectModel(vehicle) {
  const hasPhysics = vehicle.drag_cd != null && vehicle.frontal_area_m2 != null &&
                     vehicle.roll_cr != null && vehicle.drivetrain_eff != null &&
                     vehicle.regen_eff != null && vehicle.mass_kg != null;
  if (hasPhysics) return 'physics';

  const hasEmpirical = vehicle.consumption_wh_km != null && vehicle.mass_kg != null;
  if (hasEmpirical) return 'estimated';

  return 'unsupported';
}

function segmentEnergy(segment, vehicle, conditions, mode) {
  if (mode === 'physics') return segmentEnergyPhysics(segment, vehicle, conditions);
  return segmentEnergyEmpirical(segment, vehicle, conditions);
}

// ============================================================================
// MAIN PLANNER — walks route, computes SoC at every point
// ============================================================================
function planSoCAlongRoute(route, vehicle, weather_array, settings) {
  const { starting_soc_pct, hvac_setting, driving_style } = settings;
  const driving_style_multiplier = {
    efficient: 0.92, normal: 1.00, spirited: 1.10
  }[driving_style] || 1.0;

  const mode = selectModel(vehicle);
  if (mode === 'unsupported') {
    return { error: 'Insufficient vehicle data for range prediction' };
  }

  // Effective usable battery — chemistry temp + degradation
  const avg_temp = weather_array.reduce((s, w) => s + w.ambient_c, 0) / weather_array.length;
  const batt_temp_eff = batteryTempEfficiency(avg_temp, vehicle.battery_type);
  const degradation = vehicle.annual_capacity_loss_pct
    ? Math.max(0.7, 1 - (vehicle.annual_capacity_loss_pct / 100) * 4)  // assume 4 yr default
    : 1.0;
  const usable_kwh = (vehicle.battery_kwh_usable || vehicle.battery_kwh * 0.92) *
                     batt_temp_eff * degradation;

  let soc_kwh = usable_kwh * (starting_soc_pct / 100);
  const points = [];

  // For the v2 tire pressure model, we need cumulative trip duration per
  // segment so the warming model can ramp correctly. We compute this as
  // we walk the route.
  let cumulative_time_min = 0;

  for (let i = 0; i < route.segments.length; i++) {
    const seg = route.segments[i];
    const wx = weather_array[i] || weather_array[weather_array.length - 1];

    // Use the cumulative time at the START of this segment for the warming
    // model — by the time we're driving this segment, the tires have been
    // warming for `cumulative_time_min` already.
    const segment_trip_duration_min = cumulative_time_min;

    const conditions = {
      ambient_c: wx.ambient_c,
      altitude_m: seg.altitude_m || 0,
      wind_speed_kmh: wx.wind_speed_kmh,
      wind_direction_deg: wx.wind_direction_deg,
      hvac_setting,
      driving_style_multiplier,
      tire_pressure_actual_kpa: settings.tire_pressure_actual_kpa,
      // v2 tire pressure context — all optional. Caller sets these in
      // settings to opt into the temperature/permeation/warming model.
      days_since_tire_check: settings.days_since_tire_check,
      trip_duration_min: segment_trip_duration_min,
      inflation_gas: settings.inflation_gas,
      measured_frequency_hz: settings.measured_frequency_hz,
      tire_size_code: settings.tire_size_code,
      // Drive-cycle context — optional. country_code can come from settings
      // (trip-level) or from the segment (more accurate for cross-border
      // routes). Segment-level wins if both present. road_type lets a future
      // road-classification pipeline override the default speed-based curve.
      country_code: seg.country_code || settings.country_code,
      road_type: seg.road_type || settings.road_type
    };

    const result = segmentEnergy(seg, vehicle, conditions, mode);
    soc_kwh -= result.energy_wh / 1000;
    cumulative_time_min += result.time_s / 60;

    points.push({
      km_in: route.segments.slice(0, i + 1).reduce((s, x) => s + x.distance_km, 0),
      soc_pct: Math.max(0, (soc_kwh / usable_kwh) * 100),
      soc_kwh,
      segment_energy_wh: result.energy_wh,
      drive_wh: result.drive_wh,
      hvac_wh: result.hvac_wh,
      time_s: result.time_s,
      mode: result.mode,
      // Per-segment conditions (for UI display, debugging, and the audit panel)
      avg_speed_kmh: seg.avg_speed_kmh,
      distance_km: seg.distance_km,
      ambient_c: wx.ambient_c,
      wind_speed_kmh: wx.wind_speed_kmh,
      wind_direction_deg: wx.wind_direction_deg,
      gradient_pct: seg.gradient_pct,
      // Engine diagnostics (drive cycle, tire pressure, drivetrain, etc.)
      diagnostics: result.diagnostics || null
    });
  }

  return {
    mode,
    usable_kwh,
    points,
    final_soc_pct: points.length ? points[points.length - 1].soc_pct : starting_soc_pct,
    total_energy_wh: points.reduce((s, p) => s + p.segment_energy_wh, 0),
    total_time_s: points.reduce((s, p) => s + p.time_s, 0),
    confidence: confidenceBandPct(vehicle, mode)
  };
}

// ============================================================================
// CONFIDENCE BAND — Monte Carlo-style but cheap
// ============================================================================
function confidenceBandPct(vehicle, mode) {
  // Base uncertainty by data quality
  const dq_band = {
    'Verified-EPA-OEV': 3.0,
    'Verified-EEA': 3.0,
    'Verified-NRCan-OEV': 3.0,
    'Verified-OEM': 4.0,
    'Verified-OEM-Stated': 4.5,
    'Verified-Independent-Test': 4.0,
    'Triangulated-Aggregator': 6.0,
    'Partial-EPA': 6.0,
    'Partial-OEM-Stated': 7.0,
    'Partial-AIS-Compliant': 8.0,
    'Stub': 15.0
  }[vehicle.data_quality] || 8.0;

  // Mode adds uncertainty
  const mode_band = mode === 'physics' ? 0 : (mode === 'estimated' ? 3.0 : 6.0);

  // Combine in quadrature (treating as independent uncertainties)
  return Math.sqrt(dq_band ** 2 + mode_band ** 2);
}

// ============================================================================
// OPTIMAL CHARGE % FOR NEXT LEG
// ============================================================================
function optimalChargePctForNextLeg(distance_to_next_charger_km, vehicle, conditions, reserve_pct = 20) {
  // The cheapest math: how much SoC do I need to reach next charger + reserve?
  // Above 60-80%, charging curve drops off sharply — so always undershoot if possible.

  const consumption_per_km_wh = vehicle.consumption_wh_km;  // empirical baseline
  const usable_kwh = vehicle.battery_kwh_usable || vehicle.battery_kwh * 0.92;
  const energy_needed_wh = distance_to_next_charger_km * consumption_per_km_wh * 1.10;  // 10% safety
  const energy_needed_pct = (energy_needed_wh / 1000 / usable_kwh) * 100;

  const required_soc = reserve_pct + energy_needed_pct;
  // Cap at 80% by default — charging above 80% is slow and rarely worth it
  return Math.min(80, Math.max(reserve_pct + 5, Math.ceil(required_soc / 5) * 5));
}

// ============================================================================
// PRECONDITIONING WINDOW RECOMMENDATION
// ============================================================================
function preconditioningWindowMin(vehicle, ambient_c, distance_to_next_stop_km, avg_speed_kmh = 90) {
  if (!vehicle.preconditioning_supported) return 0;

  // Time-to-arrival
  const ttа_min = (distance_to_next_stop_km / avg_speed_kmh) * 60;

  // Required warm-up time depends on starting battery temp
  let needed_min = 15;  // mild conditions
  if (ambient_c < 0) needed_min = 30;
  else if (ambient_c < 10) needed_min = 20;
  else if (ambient_c > 35) needed_min = 10;  // pre-cool

  return Math.min(ttа_min, needed_min);
}

// ============================================================================
// EXPORTS
// ============================================================================
export {
  planSoCAlongRoute,
  optimalChargePctForNextLeg,
  preconditioningWindowMin,
  selectModel,
  segmentEnergy,
  airDensity,
  hvacPower_kW,
  batteryTempEfficiency,
  windComponentAlongRoute,
  confidenceBandPct,
  tirePressurePenalty,
  tirePressurePenaltyV2,
  drivetrainEfficiency,
  drivetrainMassAdder_kg,
  roadQualityMultiplier
};

// Re-export from driveCycle.js so callers have one import surface.
export { driveCycleMultiplier } from './driveCycle.js';
